import { useAutoneTranslation } from '@autone/translations';
import {
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  DataTableHeaderCellWithSorting,
  DataTableIntegerValue,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { type APP_NAME } from '../../../consts';
import { useAssortmentTableContext } from '../contexts';

import { type CommonAssortmentTableItem } from './common-columns.types';

const COLUMN_ID = 'total_sales_units' as const;

export type TotalSalesUnitsColumnId = typeof COLUMN_ID;

interface TotalSalesUnitsFields {
  lifetime_sales_units: CommonAssortmentTableItem['lifetime_sales_units'];
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<TotalSalesUnitsFields>();

export const createTotalSalesUnitsColumn = <
  T extends TotalSalesUnitsFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.lifetime_sales_units),
    {
      id: COLUMN_ID,
      size: 175,
      header: function TotalSalesHeader({ column }) {
        const { t } = useAutoneTranslation<typeof APP_NAME>();
        const tableName = useAssortmentTableContext();

        const headerCellContent = useMemo(
          () => (
            <DataTableCellContent align={'right'}>
              <DataTableCellTitle className="uppercase font-medium whitespace-normal text-right">
                {t('table-columns.total-sales-units')}
              </DataTableCellTitle>
            </DataTableCellContent>
          ),
          [t],
        );

        if (tableName === 'skuLocationsTable') return headerCellContent;

        return (
          <DataTableHeaderCellWithSorting
            column={column}
            arrowPosition="left"
            showSortable
          >
            {headerCellContent}
          </DataTableHeaderCellWithSorting>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function TotalSalesCell({ getValue }) {
            return (
              <DataTableCellContent align="right">
                <DataTableCellTitle>
                  <DataTableIntegerValue value={getValue()} />
                </DataTableCellTitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
